(function () {
  const SELECTOR = '[data-sutyo-title-transfer]';
  const instances = new WeakMap();

  function parseJSONScript(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return [];

    try {
      const value = JSON.parse(node.textContent || '[]');
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function stopModalPlayback(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;

    const iframe = dialog.querySelector('iframe');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.removeAttribute('src');
    }

    const video = dialog.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.muted = true;
      video.defaultMuted = true;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        // Some browsers can block currentTime resets during teardown.
      }
    }
  }

  function setupModal(root, sectionId) {
    const openButton = root.querySelector('[data-video-open]');
    const dialog = document.getElementById('SutyoTitleTransferDialog-' + sectionId);
    const closeButton = dialog?.querySelector?.('[data-video-close]');

    if (!(dialog instanceof HTMLDialogElement) || !openButton) {
      return function cleanupModal() {};
    }

    function handleOpen() {
      dialog.showModal();

      window.requestAnimationFrame(function () {
        const iframe = dialog.querySelector('iframe');
        if (iframe instanceof HTMLIFrameElement && !iframe.getAttribute('src')) {
          const iframeSrc = iframe.dataset.videoSrc;
          if (iframeSrc) {
            iframe.setAttribute('src', iframeSrc);
          }
        }

        const video = dialog.querySelector('video');
        if (video instanceof HTMLVideoElement) {
          video.defaultMuted = false;
          video.muted = false;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {});
          }
        }
      });
    }

    function handleClose() {
      dialog.close();
    }

    function handleDialogClose() {
      if (!dialog.open) {
        stopModalPlayback(dialog);
      }
    }

    function handleDialogClick(event) {
      if (event.target === dialog) {
        dialog.close();
      }
    }

    function handleKeydown(event) {
      if (event.key === 'Escape' && dialog.open) {
        dialog.close();
      }
    }

    openButton.addEventListener('click', handleOpen);
    closeButton?.addEventListener('click', handleClose);
    dialog.addEventListener('close', handleDialogClose);
    dialog.addEventListener('click', handleDialogClick);
    document.addEventListener('keydown', handleKeydown);

    return function cleanupModal() {
      openButton.removeEventListener('click', handleOpen);
      closeButton?.removeEventListener('click', handleClose);
      dialog.removeEventListener('close', handleDialogClose);
      dialog.removeEventListener('click', handleDialogClick);
      document.removeEventListener('keydown', handleKeydown);
      stopModalPlayback(dialog);
    };
  }

  function createSequence(root) {
    if (!root || instances.has(root)) {
      return;
    }

    const sectionId = root.dataset.sectionId || '';
    const canvas = root.querySelector('[data-sequence-canvas]');
    const animationSection = root.querySelector('[data-sequence-scroll]');
    const cover = root.querySelector('[data-sequence-cover]');
    const overlayNodes = root.querySelectorAll('[data-overlay-item]');
    const desktopUrls = parseJSONScript(root, '[data-sequence-desktop]');
    const mobileUrls = parseJSONScript(root, '[data-sequence-mobile]');
    const cleanupModal = setupModal(root, sectionId);

    if (!(canvas instanceof HTMLCanvasElement) || !animationSection || !desktopUrls.length) {
      root.classList.add('is-static');
      instances.set(root, {
        cleanup() {
          cleanupModal();
        }
      });
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    let gsapContext = null;
    let currentTween = null;
    let resizeTimer = 0;
    let textVisible = false;
    let currentMode = null;
    let loadedImages = [];

    function showCover() {
      cover?.classList.remove('is-hidden');
    }

    function hideCover() {
      cover?.classList.add('is-hidden');
    }

    function drawStaticImage(url, isMobile) {
      if (!url) return;

      showCover();

      canvas.width = isMobile ? 2160 : 3840;
      canvas.height = isMobile ? 2880 : 1920;

      const image = new Image();
      image.src = url;
      image.onload = function () {
        loadedImages = [image];
        drawFrame(0);
      };
    }

    function setTextHidden() {
      if (!window.gsap) return;
      window.gsap.set(overlayNodes, { autoAlpha: 0, y: 24 });
      textVisible = false;
    }

    function playTextIn() {
      if (!window.gsap || textVisible) return;
      textVisible = true;

      overlayNodes.forEach(function (node, index) {
        window.gsap.killTweensOf(node);
        window.gsap.to(node, {
          autoAlpha: 1,
          y: 0,
          duration: 0.72,
          ease: 'power2.out',
          delay: index * 0.1
        });
      });
    }

    function playTextOut() {
      if (!window.gsap || !textVisible) return;
      textVisible = false;

      Array.from(overlayNodes)
        .reverse()
        .forEach(function (node, index) {
          window.gsap.killTweensOf(node);
          window.gsap.to(node, {
            autoAlpha: 0,
            y: 24,
            duration: 0.5,
            ease: 'power2.in',
            delay: index * 0.08
          });
        });
    }

    function drawFrame(index) {
      const image = loadedImages[index];
      if (!image || !image.complete) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    }

    function buildSequence(urls) {
      const playhead = { frame: 0 };
      let currentFrame = -1;
      let loadedCount = 0;
      const totalImages = urls.length;

      showCover();

      loadedImages = urls.map(function (url, index) {
        const image = new Image();
        const handleImageReady = function () {
          loadedCount += 1;

          if (index === 0) {
            drawFrame(0);
          }

          if (loadedCount >= totalImages) {
            hideCover();
          }
        };

        image.onload = handleImageReady;
        image.onerror = handleImageReady;

        image.src = url;

        return image;
      });

      return window.gsap.to(playhead, {
        frame: Math.max(urls.length - 1, 0),
        ease: 'none',
        duration: urls.length / 30,
        paused: false,
        onUpdate: function () {
          const nextFrame = Math.round(playhead.frame);
          if (nextFrame !== currentFrame) {
            drawFrame(nextFrame);
            currentFrame = nextFrame;
          }
        },
        scrollTrigger: {
          trigger: animationSection,
          start: 'top top',
          end: 'bottom bottom',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: function (self) {
            if (self.progress >= 0.5) {
              playTextIn();
            } else {
              playTextOut();
            }
          }
        }
      });
    }

    function initMode(isMobile) {
      if (!window.gsap || !window.ScrollTrigger) {
        root.classList.add('is-static');
        drawStaticImage((isMobile && mobileUrls.length ? mobileUrls : desktopUrls)[0], isMobile);
        return;
      }
      if (prefersReducedMotion.matches) {
        root.classList.add('is-static');
        drawStaticImage((isMobile && mobileUrls.length ? mobileUrls : desktopUrls)[0], isMobile);
        return;
      }

      const urls = isMobile && mobileUrls.length ? mobileUrls : desktopUrls;
      const nextMode = isMobile ? 'mobile' : 'desktop';

      if (!urls.length || currentMode === nextMode) {
        return;
      }

      currentMode = nextMode;
      root.classList.remove('is-static');
      showCover();

      currentTween?.kill();
      currentTween = null;

      if (gsapContext) {
        gsapContext.revert();
      }

      canvas.width = isMobile ? 2160 : 3840;
      canvas.height = isMobile ? 2880 : 1920;

      gsapContext = window.gsap.context(function () {
        setTextHidden();
        currentTween = buildSequence(urls);
      }, root);

      window.ScrollTrigger.refresh();
    }

    function handleMediaChange(event) {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        initMode(event.matches);
      }, 180);
    }

    function handleReducedMotionChange() {
      if (prefersReducedMotion.matches) {
        currentTween?.kill();
        currentTween = null;
        gsapContext?.revert();
        gsapContext = null;
        root.classList.add('is-static');
        if (loadedImages[0]) {
          drawFrame(0);
        }
        showCover();
        return;
      }

      initMode(mobileQuery.matches);
    }

    mobileQuery.addEventListener('change', handleMediaChange);
    prefersReducedMotion.addEventListener('change', handleReducedMotionChange);
    initMode(mobileQuery.matches);

    instances.set(root, {
      cleanup() {
        cleanupModal();
        mobileQuery.removeEventListener('change', handleMediaChange);
        prefersReducedMotion.removeEventListener('change', handleReducedMotionChange);
        window.clearTimeout(resizeTimer);
        currentTween?.kill();
        gsapContext?.revert();
        if (window.ScrollTrigger) {
          window.ScrollTrigger.getAll().forEach(function (trigger) {
            if (trigger.vars && trigger.vars.trigger && root.contains(trigger.vars.trigger)) {
              trigger.kill();
            }
          });
        }
        loadedImages = [];
      }
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(createSequence);
  }

  function destroySection(sectionId) {
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      if (root.dataset.sectionId !== sectionId) return;

      const instance = instances.get(root);
      if (instance) {
        instance.cleanup();
        instances.delete(root);
      }
    });
  }

  function initWhenReady() {
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady, { once: true });
  } else {
    initWhenReady();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initWhenReady();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroySection(event.detail.sectionId);
  });
})();
